/**
 * ValidationFieldRow - Individual field row for validation
 *
 * Displays a field with checkbox, label, value, and edit capability
 */

import { Check, Pencil, X } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { useValidationStore } from '../../store';

export interface ValidationFieldRowProps {
  category: 'transaction' | 'loanTerms' | 'parties';
  fieldKey: string;
  label: string;
  type: 'text' | 'number' | 'currency' | 'percentage' | 'date' | 'boolean';
  originalValue: string | number | boolean;
  suffix?: string;
}

export function ValidationFieldRow({
  category,
  fieldKey,
  label,
  type,
  originalValue,
  suffix = '',
}: ValidationFieldRowProps) {
  const { selectedFields, toggleField, setFieldValue } = useValidationStore();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(String(originalValue));

  // Handle different category types (Record vs Array for parties)
  const categoryData = selectedFields[category];
  const field =
    category && Array.isArray(categoryData)
      ? undefined
      : (categoryData as Record<string, any>)?.[fieldKey];
  const isChecked = field?.include ?? true;
  const currentValue = (field?.value ?? originalValue) as string | number | boolean | undefined;
  const wasModified = field?.wasModified ?? false;

  const formatValue = (val: string | number | boolean | undefined): string => {
    if (val === undefined || val === null) return '—';
    if (type === 'boolean') {
      return val === true ? 'Yes' : 'No';
    }
    if (type === 'currency') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(Number(val));
    }
    if (type === 'percentage') {
      return `${Number(val) * 100}%`;
    }
    return String(val);
  };

  const handleSave = () => {
    let parsedValue: string | number | boolean = editValue;

    if (type === 'boolean') {
      parsedValue = editValue === 'true' || editValue === 'Yes';
    } else if (type === 'number' || type === 'currency') {
      parsedValue = parseFloat(editValue);
      if (isNaN(parsedValue as number)) {
        parsedValue = originalValue;
      }
    } else if (type === 'percentage') {
      parsedValue = parseFloat(editValue) / 100;
      if (isNaN(parsedValue as number)) {
        parsedValue = originalValue;
      }
    }

    setFieldValue(category, fieldKey, parsedValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(String(currentValue));
    setIsEditing(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
        isChecked ? 'bg-background' : 'bg-muted/30 opacity-60'
      }`}
    >
      {/* Checkbox */}
      <Checkbox
        checked={isChecked}
        onCheckedChange={() => toggleField(category, fieldKey)}
        className="shrink-0"
      />

      {/* Label */}
      <div className="flex-1 min-w-0">
        <label className="text-sm font-medium block">{label}</label>
        {wasModified && (
          <span className="text-xs text-amber-600 dark:text-amber-400">Modified</span>
        )}
      </div>

      {/* Value */}
      <div className="flex items-center gap-2 flex-1 justify-end">
        {isEditing ? (
          <div className="flex items-center gap-2 flex-1 max-w-xs">
            <Input
              type={type === 'number' || type === 'currency' ? 'number' : 'text'}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyPress}
              className="h-8"
              autoFocus
            />
            {suffix && <span className="text-sm text-muted-foreground">{suffix}</span>}
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={handleSave}>
              <Check className="h-4 w-4 text-green-600" />
            </Button>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={handleCancel}>
              <X className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-sm font-mono">{formatValue(currentValue)}</span>
            {suffix && <span className="text-sm text-muted-foreground">{suffix}</span>}
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6"
              onClick={() => {
                setEditValue(String(currentValue));
                setIsEditing(true);
              }}
            >
              <Pencil className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
